import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { ApiError } from '@/api/client'
import { mePeriodQuery, trainerSessionsQuery, trainersReportQuery, type MePeriod, type SessionWithBookings } from '@/api/payroll'
import { classTypesQuery } from '@/api/services'
import { MarkControl, MarkLine } from '@/app/payroll/attendance'
import { PayoutStateBadge, StatusChip } from '@/app/payroll/badges'
import { useNow } from '@/app/access/use-now'
import { MonthBar, type PayrollContext } from '@/app/payroll/payroll-shell'
import { usePayrollContext } from '@/app/payroll/use-payroll'
import { NothingForRole } from '@/app/nothing-for-role'
import { hhmm, wallOf } from '@/domain/calendar'
import { errorText } from '@/domain/errors'
import { formatMoney } from '@/domain/pass-rules'
import { balanceText, dayTime, dayTitle, markable, runsSessions, signedMoney, upcomingRange } from '@/domain/payroll'
import { payrollErrorText } from '@/domain/payroll-errors'
import { canSeeServices, monthOf } from '@/domain/services'
import { formatDate, t } from '@/i18n'
import { cn } from '@/lib/cn'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile, StatTiles } from '@/ui/stat-tile'

type Search = { month?: string }

export const Route = createFileRoute('/_app/c/$orgId/my-work')({
  validateSearch: (search: Record<string, unknown>): Search =>
    typeof search['month'] === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(search['month']) ? { month: search['month'] } : {},
  component: MyWork,
})

/// «Моя робота» (specs/payroll-periods.md §6–§7): the caller's own sessions of
/// the days ahead with the people booked on them and «Прийшов / Не прийшов»
/// (the trainer marks their own class until the end of the next day, PT1);
/// the month's sessions held; and their pay — нараховано or затверджено, the
/// salary, adjustments with their comments, payouts, the balance and the
/// forecast. Any member reads their own line; a trainer opens the company here.
function MyWork() {
  const { orgId } = Route.useParams()
  const { caller, pending, failed, retry, ctx } = usePayrollContext(orgId)
  const m = t.payroll.myWork
  if (!caller.member) return <NothingForRole />
  return (
    <Page wide>
      <PageHeader title={m.title} subtitle={m.subtitle} />
      {pending ? (
        <LoadingBlock label={t.app.loading} />
      ) : failed ? (
        <InlineBanner kind="error" message={errorText(failed)} action={{ label: t.app.retry, onClick: retry }} />
      ) : (
        <MyWorkScreen ctx={ctx()} />
      )}
    </Page>
  )
}

function MyWorkScreen({ ctx }: { ctx: PayrollContext }) {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const thisMonth = monthOf(wallOf(new Date(), ctx.zone).date)
  const month = search.month ?? thisMonth
  const trainer = runsSessions(ctx.caller)
  const go = (next: string) => void navigate({ search: next === thisMonth ? {} : { month: next }, replace: true })

  return (
    <div className="flex max-w-[960px] flex-col gap-10">
      {trainer ? <Upcoming ctx={ctx} /> : null}
      <section className="flex flex-col gap-5">
        <MonthBar month={month} max={thisMonth} onChange={go} />
        {trainer ? <MonthStats ctx={ctx} month={month} /> : null}
        <MyPay ctx={ctx} month={month} />
      </section>
    </div>
  )
}

// ---- the days ahead ----

function Upcoming({ ctx }: { ctx: PayrollContext }) {
  const m = t.payroll.myWork
  const now = useNow(60_000)
  const range = upcomingRange(now, ctx.zone)
  const sessions = useQuery(trainerSessionsQuery(ctx.orgId, { from: range.from, to: range.to, trainerUserId: 'me' }))
  const types = useQuery(classTypesQuery(ctx.orgId))
  const typeName = new Map((types.data ?? []).map((x) => [x.id, x.name]))

  const days = [...new Set((sessions.data ?? []).map((s) => wallOf(new Date(s.startsAt), ctx.zone).date))].sort()
  // Yesterday only while something there is still to mark.
  const shown = days.filter(
    (day) =>
      day >= range.today ||
      (sessions.data ?? []).some(
        (s) => wallOf(new Date(s.startsAt), ctx.zone).date === day && s.status !== 'cancelled' && s.bookings.some((b) => b.status === 'booked'),
      ),
  )

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title={m.upcoming} inset={false} />
      {sessions.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : sessions.isError ? (
        <InlineBanner kind="error" message={errorText(sessions.error)} action={{ label: t.app.retry, onClick: () => void sessions.refetch() }} />
      ) : shown.length === 0 ? (
        <EmptyState icon={<CalendarCheck />} title={m.upcomingEmpty} className="py-8" />
      ) : (
        shown.map((day) => (
          <div key={day} className="flex flex-col gap-2">
            <h3 className="px-1 text-callout font-semibold text-text first-letter:uppercase">{dayTitle(day, range.today)}</h3>
            {(sessions.data ?? [])
              .filter((s) => wallOf(new Date(s.startsAt), ctx.zone).date === day)
              .map((session) => (
                <SessionCard
                  key={session.id}
                  ctx={ctx}
                  now={now}
                  session={session}
                  title={typeName.get(session.classTypeId) ?? t.services.unknownService}
                />
              ))}
          </div>
        ))
      )}
    </section>
  )
}

function SessionCard({ ctx, session, title, now }: { ctx: PayrollContext; session: SessionWithBookings; title: string; now: Date }) {
  const m = t.payroll.myWork
  const a = t.payroll.attendance
  const start = wallOf(new Date(session.startsAt), ctx.zone).minutes
  const end = wallOf(new Date(session.endsAt), ctx.zone).minutes
  const places = session.bookings.filter((b) => b.status !== 'cancelled')
  const cancelled = session.status === 'cancelled'
  const started = Date.parse(session.startsAt) <= now.getTime()
  return (
    <article className={cn('overflow-hidden rounded-[16px] bg-surface', cancelled && 'opacity-60')}>
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-4 pt-3.5 pb-2.5">
        <span className="text-callout font-semibold text-text tabular">
          {hhmm(start)}–{hhmm(end)}
        </span>
        <span className="min-w-0 flex-1 truncate text-callout text-text">{title}</span>
        <span className="text-caption text-muted tabular">
          {cancelled ? m.cancelledSession : session.appointment ? '' : m.places(places.length, session.capacity)}
        </span>
      </header>
      {cancelled ? null : places.length === 0 ? (
        <p className="px-4 pb-3.5 text-caption text-muted">{m.noBookings}</p>
      ) : (
        <ul className="divide-y divide-surface-3 border-t border-surface-3">
          {places.map((place) => {
            const who = place.clientName ?? t.passes.sold.unknownClient
            return (
              <li key={place.id} className="flex flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:gap-3">
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-callout font-medium text-text">{who}</span>
                  {place.lastMark ? (
                    <MarkLine mark={place.lastMark} zone={ctx.zone} />
                  ) : (
                    <span className="text-caption text-muted">{started ? a.booked : m.notStarted}</span>
                  )}
                </div>
                {markable(session.startsAt, place.status, now) ? (
                  <MarkControl orgId={ctx.orgId} bookingId={place.id} status={place.status} label={who} className="items-start sm:items-end" />
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </article>
  )
}

// ---- the month ----

function MonthStats({ ctx, month }: { ctx: PayrollContext; month: string }) {
  const s = t.payroll.myWork.stats
  const report = useQuery(trainersReportQuery(ctx.orgId, month, ctx.zone))
  if (report.isPending) return <LoadingBlock label={t.app.loading} />
  if (report.isError) {
    // A wider scope without `schedule ≥ view` reads no trainer's month — not
    // even their own here; nothing to say about it.
    if (report.error instanceof ApiError && report.error.status === 403) return null
    return <InlineBanner kind="stale" message={t.payroll.myWork.statsUnavailable} />
  }
  const row = report.data.trainers.find((x) => x.trainerUserId === ctx.access.userId)
  const held = row?.held ?? 0
  return (
    <StatTiles className="grid-cols-2 lg:grid-cols-4">
      <StatTile label={s.held} value={String(held)} note={s.heldNote(row?.personal ?? 0, row?.group ?? 0)} />
      <StatTile label={s.attendees} value={String(row?.attendees ?? 0)} />
      <StatTile label={s.noShows} value={String(row?.noShows ?? 0)} />
      <StatTile
        label={s.unmarked}
        value={String(row?.unmarked ?? 0)}
        tone={(row?.unmarked ?? 0) > 0 ? 'warning' : 'normal'}
        note={(row?.unmarked ?? 0) > 0 ? s.unmarkedNote : undefined}
      />
    </StatTiles>
  )
}

function MyPay({ ctx, month }: { ctx: PayrollContext; month: string }) {
  const m = t.payroll.myWork
  const p = t.payroll
  const me = useQuery(mePeriodQuery(ctx.orgId, month, ctx.zone))
  if (me.isPending) return <LoadingBlock label={t.app.loading} />
  if (me.isError) {
    return <InlineBanner kind="error" message={errorText(me.error, payrollErrorText())} action={{ label: t.app.retry, onClick: () => void me.refetch() }} />
  }
  const data = me.data
  const line = data.line
  const approved = data.status === 'approved'
  return (
    <div className={cn('flex flex-col gap-4', me.isPlaceholderData && 'opacity-60 transition-opacity')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pt-2">
        <h3 className="text-heading font-bold text-text">{m.pay}</h3>
        <StatusChip tone={approved ? 'success' : 'normal'}>{approved ? p.status.approved : p.status.open}</StatusChip>
        <span className="text-caption text-text-2">
          {approved && data.approvedAt ? m.payNoteApproved(dayTime(data.approvedAt, data.timeZone)) : m.payNoteOpen}
        </span>
      </div>
      <StatTiles className="grid-cols-2 lg:grid-cols-4">
        <StatTile label={approved ? p.tiles.approved : p.tiles.accrued} value={formatMoney(line.totalMinor)} />
        <StatTile label={p.tiles.paid} value={formatMoney(line.paidMinor)} note={<PayoutStateBadge state={line.payoutState} />} />
        <StatTile
          label={p.tiles.balance}
          value={balanceText(line.balanceMinor)}
          tone={line.balanceMinor > 0 ? 'warning' : line.balanceMinor < 0 ? 'danger' : 'normal'}
        />
        <StatTile
          label={m.forecast}
          value={formatMoney(data.forecast.plannedMinor)}
          note={data.forecast.sessions > 0 ? m.forecastNote(data.forecast.sessions) : undefined}
        />
      </StatTiles>
      {data.changesAfterApproval && data.changesAfterApproval.deltaMinor !== 0 ? (
        <InlineBanner kind="stale" message={m.changed(signedMoney(data.changesAfterApproval.deltaMinor))} />
      ) : null}
      <div className="grid gap-6 lg:grid-cols-2">
        <Breakdown data={data} />
        <Payouts data={data} />
      </div>
      {canSeeServices(ctx.caller) && runsSessions(ctx.caller) ? (
        <Link
          to="/c/$orgId/services/analytics"
          params={{ orgId: ctx.orgId }}
          search={month === monthOf(wallOf(new Date(), ctx.zone).date) ? {} : { month }}
          className="self-start px-1 text-callout font-medium text-text-2 underline-offset-2 hover:text-text hover:underline"
        >
          {m.analytics}
        </Link>
      ) : null}
    </div>
  )
}

const Rows = ({ title, children, empty }: { title: string; children: ReactNode; empty?: string | null }) => (
  <section className="flex flex-col gap-2">
    <SectionHeader title={title} inset={false} />
    {empty ? (
      <InlineBanner kind="empty" message={empty} />
    ) : (
      <ul className="divide-y divide-surface-3 overflow-hidden rounded-[16px] bg-surface">{children}</ul>
    )}
  </section>
)

const Row = ({ title, subtitle, value, tone = 'normal' }: { title: string; subtitle?: string | null; value: string; tone?: 'normal' | 'danger' | 'strong' }) => (
  <li className="flex items-start gap-3 px-4 py-3">
    <span className="flex min-w-0 flex-1 flex-col">
      <span className={cn('text-callout text-text', tone === 'strong' ? 'font-semibold' : 'font-medium')}>{title}</span>
      {subtitle ? <span className="text-caption text-pretty text-text-2">{subtitle}</span> : null}
    </span>
    <span className={cn('shrink-0 text-callout tabular', tone === 'danger' ? 'text-danger' : 'text-text', tone === 'strong' ? 'font-bold' : 'font-semibold')}>
      {value}
    </span>
  </li>
)

const Breakdown = ({ data }: { data: MePeriod }) => {
  const m = t.payroll.myWork
  const p = t.payroll
  const line = data.line
  const counts = line.counts
  return (
    <Rows title={m.breakdown}>
      <Row title={m.sessionsLine} subtitle={p.person.sessionsNote(counts.confirmed, counts.planned, counts.noShow)} value={formatMoney(line.sessionsMinor)} />
      <Row title={m.salaryLine} subtitle={data.salary?.comment ?? null} value={line.salaryMinor ? formatMoney(line.salaryMinor) : '—'} />
      {data.adjustments.map((row) => (
        <Row
          key={row.id}
          title={p.adjustment.kinds[row.kind] ?? row.kind}
          subtitle={row.comment}
          value={signedMoney(row.amountMinor)}
          tone={row.amountMinor < 0 ? 'danger' : 'normal'}
        />
      ))}
      <Row title={data.status === 'approved' ? p.tiles.approved : p.tiles.accrued} value={formatMoney(line.totalMinor)} tone="strong" />
    </Rows>
  )
}

const Payouts = ({ data }: { data: MePeriod }) => {
  const m = t.payroll.myWork
  const p = t.payroll
  return (
    <Rows title={m.payouts} empty={data.payouts.length === 0 ? m.payoutsEmpty : null}>
      {data.payouts.map((row) => (
        <Row
          key={row.id}
          title={p.payout.kinds[row.kind] ?? row.kind}
          subtitle={[p.payout.line(p.payout.methods[row.method] ?? row.method, formatDate(row.paidOn)), row.comment].filter(Boolean).join(' · ')}
          value={formatMoney(row.amountMinor)}
        />
      ))}
      {data.payouts.length > 0 ? <Row title={p.tiles.balance} value={balanceText(data.line.balanceMinor)} tone="strong" /> : null}
    </Rows>
  )
}
